// @flow
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { Map as ImmutableMap } from 'immutable';

import { getLogger } from 'utils/logger';

import { getQcsSyncResources, qcsSyncUpdateResource, qcsSyncDeleteResource }
  from 'data/qcsSyncResourceSagaHelpers';
import { mkQcsSyncResourceConfig } from 'data/qcsSyncResourceTypes';

import { createDialog } from 'data/rootUi/actions';
import { mkRootUiData } from 'data/rootUi/types';
import {
  DIALOG_TYPE as DIALOG_ACCOUNT_DISCOVERY,
  mkAccountDiscoveryDialogProps,
} from 'components/Dialogs/AccountDiscovery/types';
import { getInstitution } from 'data/institutions/actions';
import { getInstitutionsById } from 'data/institutions/selectors';
import store from 'store';

import * as actions from './actions';
// import { getAccounts } from '../accounts/actions'
import {
  getLastSyncDate,
  getCredentialBlobsForRefreshingLogins,
  getCredentialsForRefreshingLogins,
  wasUserActionCancelled,
} from './selectors';
import { transformResponseToInstitutionLogins, transformInstitutionLoginToRequestData } from './transformers';
import type { InstitutionLogin } from './types';

const log = getLogger('data/categories/sagas');

// =============================================================================
// Refresh Institution Logins
// =============================================================================

export function* preRefreshAccountsCheck(loginsToRefresh: Array<InstitutionLogin>, action: any): Generator<*, *, *> {
  // log.debug('Starting RefreshAccounts PreCheck');

  const credentialBlobsForRefreshingLogins = yield select(getCredentialBlobsForRefreshingLogins);
  const credentialsForRefreshingLogins = yield select(getCredentialsForRefreshingLogins);

  for (let i = 0; i < loginsToRefresh.length; ++i) {
    const login = loginsToRefresh[i];
    if (login.ewcLive) {
      log.debug('Found EWCLive account');
      if (
        credentialBlobsForRefreshingLogins.get(login.id) ||
        credentialsForRefreshingLogins.get(login.id)
      ) {
        // already have credentials for this login so continue on to checking other logins
        // log.debug('Already have credentials, continuing to next login');
        continue;   // eslint-disable-line
      }
      const userActionCancelled = yield select(wasUserActionCancelled, login.id);
      if (userActionCancelled) {
        // already asked user for credentials, but they cancelled so we won't ask again
        // log.debug('User cancelled credentials, continuing to next login');
        continue;   // eslint-disable-line
      }

      // ask user for credentials, then trigger refresh again
      const dialogData = mkRootUiData({
        id: login.id,
        type: DIALOG_ACCOUNT_DISCOVERY,
        allowNesting: false,
        props: ImmutableMap(mkAccountDiscoveryDialogProps({
          mode: 'PROVIDE-CREDENTIALS',
          institutionLogin: login,

          afterCloseAction: action,
        })),
      });

      log.log('Triggering Credential Request', dialogData);
      yield put(createDialog(dialogData));
      yield put(actions.refreshAccountsTriggeredCredentialsRequest(login.id));

      // terminate refesh (will trigger again after handling credentials for institution-login)
      return false;
    }
  }

  return true;
}

function* triggerGetAccountsAction() {
  // we now deal with deleted institution id's directly in the accounts reducer
  // removing them from REDUX, so no need to call QCS at this point
  // yield put(getAccounts());
}

// =============================================================================
// Watchers
// =============================================================================

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'institution-login',
  resourceBaseUrl: '/institution-logins',
  getLastSyncDate,

  transformResponseToResources: transformResponseToInstitutionLogins,
  transformResourceToRequestData: transformInstitutionLoginToRequestData,

  successAction,
  failureAction,
});

function* getInstitutionLoginsActionWatcher() : Generator<*, *, *> {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getInstitutionLogins);

    const config = mkResourceConfig(actions.getInstitutionLoginsSuccess, actions.getInstitutionLoginsFailure);
    yield call(getQcsSyncResources, config, action);
  }
}

export function* getInstitutionLoginSuccessActionWatcher(): Generator<*, *, *> {
  const action = yield take(actions.getInstitutionLoginsSuccess);
  const institutions = getInstitutionsById(store.getState());
  const logins = action.payload.resources;
  for (let i = 0; i < logins.length; i++) {
    const currentLogin = logins[i];
    if (!institutions.has(currentLogin.institutionId) && currentLogin.institutionId && currentLogin.institutionId !== '0') {
      yield put(getInstitution({ institutionId: currentLogin.institutionId }));
    }
  }
}

export function* updateInstitutionLoginActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfig(actions.updateInstitutionLoginSuccess, actions.updateInstitutionLoginFailure);
  yield takeEvery(actions.updateInstitutionLogin, qcsSyncUpdateResource, config);
}

export function* deleteInstitutionLoginActionWatcher(): Generator<*, *, *> {
  const config = mkResourceConfig(actions.deleteInstitutionLoginSuccess, actions.deleteInstitutionLoginFailure);
  yield takeEvery(actions.deleteInstitutionLogin, qcsSyncDeleteResource, config);
}

export function* deleteInstitutionLoginSuccessActionWatcher(): Generator<*, *, *> {
  yield takeEvery(actions.deleteInstitutionLoginSuccess, triggerGetAccountsAction);
}

// =============================================================================
// Exports
// =============================================================================

export default [
  getInstitutionLoginsActionWatcher,
  updateInstitutionLoginActionWatcher,
  deleteInstitutionLoginActionWatcher,
  deleteInstitutionLoginSuccessActionWatcher,
  getInstitutionLoginSuccessActionWatcher,
];


